// Cliente HTTP do sistema interno. O app chama /api (proxy do Vite em dev, nginx em produção).
// Tokens da sessão no localStorage; 401 tenta um refresh (um por vez) e repete a chamada.

const BASE = "/api";
const STORAGE_KEY = "tsi.sessao";
export const SESSAO_EXPIRADA = "tsi:sessao-expirada";

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export const tokens = {
  get(): Tokens | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Tokens) : null;
    } catch {
      return null;
    }
  },
  set(t: Tokens) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    } catch {
      // navegador sem storage: a sessão dura só esta aba
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // idem
    }
  },
};

/**
 * Resultado do refresh. Só "invalida" (a API recusou o refresh token) encerra a sessão; queda de
 * rede ou API reiniciando ("falhou") não desloga ninguém.
 */
type Refresh = "ok" | "invalida" | "falhou";
let refreshing: Promise<Refresh> | null = null;

async function refresh(): Promise<Refresh> {
  const atual = tokens.get();
  if (!atual) return "invalida";
  const res = await fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ refreshToken: atual.refreshToken }),
  }).catch(() => null);
  if (res?.ok) {
    tokens.set((await res.json()) as Tokens);
    return "ok";
  }
  // Outra aba pode ter girado o token ao mesmo tempo: se o guardado mudou, vale o novo.
  if (tokens.get()?.refreshToken !== atual.refreshToken) return tokens.get() ? "ok" : "invalida";
  return res && (res.status === 401 || res.status === 403) ? "invalida" : "falhou";
}

// Login, refresh e logout não passam pelo refresh automático; /auth/me passa.
const SEM_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout"];

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Corpo binário (upload de arquivo). */
  file?: File | Blob;
}

export async function api<T>(path: string, opts: RequestOptions = {}, retry = true): Promise<T> {
  const headers: Record<string, string> = {};
  const t = tokens.get();
  if (t) headers.authorization = `Bearer ${t.accessToken}`;
  let body: BodyInit | undefined;
  if (opts.file) {
    body = opts.file;
    headers["content-type"] = opts.file.type || "application/octet-stream";
  } else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers["content-type"] = "application/json";
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { method: opts.method ?? (body ? "POST" : "GET"), headers, body });
  } catch {
    throw new ApiError(0, "sem_conexao", "Sem conexão com o servidor. Confira sua internet.");
  }

  if (res.status === 401 && retry && tokens.get() && !SEM_REFRESH.includes(path)) {
    refreshing ??= refresh().finally(() => (refreshing = null));
    const r = await refreshing;
    if (r === "ok") return api<T>(path, opts, false);
    if (r === "falhou") throw new ApiError(0, "sem_conexao", "Sem conexão com o servidor. Confira sua internet.");
    tokens.clear();
    window.dispatchEvent(new Event(SESSAO_EXPIRADA));
  }

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, data?.erro ?? "erro", data?.mensagem ?? "Não foi possível concluir. Tente de novo.");
  }
  return data as T;
}

/** Fetcher do SWR: a chave é o caminho da API. */
export const fetcher = <T,>(path: string) => api<T>(path);

/** Arquivo protegido (ex.: comprovante): baixa com o token e devolve uma URL local para abrir. */
export async function apiArquivoUrl(path: string): Promise<string> {
  const t = tokens.get();
  const res = await fetch(`${BASE}${path}`, { headers: t ? { authorization: `Bearer ${t.accessToken}` } : {} });
  if (!res.ok) throw new ApiError(res.status, "arquivo", "Não foi possível abrir o arquivo");
  return URL.createObjectURL(await res.blob());
}
